import { withErrorBoundary } from '@sentry/react';
import React from 'react';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import { useQuery, useSubscription } from 'urql';
import { assets } from '../../assets';
import Button from '../../components/buttons/button';
import { BlurredContainerM2030 } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import DataRow from '../../components/data-viewers/data-row/data-row';
import { Fallback } from '../../components/utils/error-boundary';
import { formatDate } from '../../helpers/date-formatters';
import { formatNumber } from '../../helpers/number-formatters';
import { StatusIcon, stringifyStatus } from '../../helpers/type-formatters';
import { GraphqlOperations } from '../../logic/graphql/operations';
import { Query, Status, Subscription, UnitOrderListItem } from '../../logic/graphql/types/types.d';
import { Paths } from '../../navigation/paths';
import { FormattedMessage, useIntl } from 'react-intl';

const UnitOrderDetails = (): JSX.Element => {
  const history = useHistory();
  const match = useRouteMatch();
  const intl = useIntl();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const id: string = (match.params as any).id || '';

  const [unitOrderQueryResult, reexecuteUnitOrderQuery] = useQuery<Query>({
    query: GraphqlOperations.queries.get_unit_order.queryString,
    variables: GraphqlOperations.queries.get_unit_order.createVars(id),
    pause: id === '',
    requestPolicy: 'network-only',
  });
  const unitOrder = unitOrderQueryResult.data?.get_unit_order;

  const handleUpdateUnitOrderSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionUnitOrders: UnitOrderListItem[] = [],
    response: Subscription,
  ): UnitOrderListItem[] => {
    if (response && response.update_unit_order) {
      if (
        response.update_unit_order.transaction_code ===
        unitOrderQueryResult.data?.get_unit_order.transaction_code
      ) {
        reexecuteUnitOrderQuery();
      }
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.update_unit_order.subscriptionString,
    },
    handleUpdateUnitOrderSubscription,
  );

  const handleBackClick = (): void => {
    history.goBack();
  };

  const handleCancelClick = (): void => {
    history.push(Paths.contact);
  };

  if (id === '') {
    return <Redirect to={Paths.unit_orders} />;
  }

  const title = unitOrder?.security.name
    ? unitOrder?.security.name +
      intl.formatMessage({ id: 'unitOrders.details.titleSecurityExtension' })
    : intl.formatMessage({ id: 'unitOrders.details.defaultTitle' });
  const titleWithTransactionCode = unitOrder?.transaction_code
    ? title + ' - ' + unitOrder.transaction_code
    : title;

  return (
    <DefaultScreen
      background={assets.images.exchange_order_bg}
      title={titleWithTransactionCode}
      onBackClick={handleBackClick}
      loading={unitOrderQueryResult.fetching}
      rightContent={
        <aside className="flex flex-row items-center">
          <StatusIcon status={unitOrder?.status} />
          <h2 className="ml-2">{stringifyStatus(unitOrder?.status, intl)}</h2>
        </aside>
      }
    >
      <div className="grid row-gap-1 col-gap-3  grid-cols-1 lg:grid-cols-2 mb-6">
        <div className="w-1/1">
          <BlurredContainerM2030 className="mb-1">
            <h2 className="mb-6">
              <FormattedMessage id={'unitOrders.details.overviewLabel'} />
            </h2>
            <DataRow
              label={intl.formatMessage({ id: 'unitOrders.details.directionLabel' })}
              value={intl.formatMessage({ id: `order.direction.${unitOrder?.direction}` })}
            />
            <DataRow
              label={intl.formatMessage({ id: 'unitOrders.details.accountLabel' })}
              value={unitOrder?.account}
            />
          </BlurredContainerM2030>
          <BlurredContainerM2030>
            <h2 className="mb-6">
              <FormattedMessage id={'unitOrders.details.fulfilledLabel'} />
            </h2>
            <DataRow
              label={intl.formatMessage({ id: 'unitOrders.details.fulfilledCountLabel' })}
              value={formatNumber(
                unitOrder?.fulfilled_count,
                intl.formatMessage({ id: 'unitOrders.details.pcs' }),
              )}
            />
            <DataRow
              label={intl.formatMessage({ id: 'unitOrders.details.dealPriceLabel' })}
              value={formatNumber(
                unitOrder?.deal_price,
                `${unitOrder?.security.currency.currency} / ${intl.formatMessage({
                  id: 'unitOrders.details.pcs',
                })}`,
                6,
              )}
            />
          </BlurredContainerM2030>
        </div>
        <BlurredContainerM2030>
          <h2 className="mb-6">
            <FormattedMessage id={'unitOrders.details.securityLabel'} />
          </h2>
          <DataRow
            label={intl.formatMessage({ id: 'unitOrders.details.securityNameLabel' })}
            value={
              (unitOrder?.security.name || '') +
              intl.formatMessage({ id: 'unitOrders.details.securityNameExtension' })
            }
          />
          <DataRow
            label={intl.formatMessage({ id: 'unitOrders.details.countLabel' })}
            value={formatNumber(
              unitOrder?.count,
              intl.formatMessage({ id: 'unitOrders.details.pcs' }),
            )}
          />
          <DataRow
            label={intl.formatMessage({ id: 'unitOrders.details.lastAvailableNetPriceLabel' })}
            value={formatNumber(
              unitOrder?.last_available_net_price,
              unitOrder?.security.currency.currency,
            )}
          />
          <DataRow
            label={intl.formatMessage({ id: 'unitOrders.details.purchaseValueLabel' })}
            value={formatNumber(unitOrder?.purchase_value, unitOrder?.security.currency.currency)}
          />
          <DataRow
            label={intl.formatMessage({ id: 'unitOrders.details.validityStartLabel' })}
            value={formatDate(unitOrder?.validity_start)}
          />
          <DataRow
            label={intl.formatMessage({ id: 'unitOrders.details.validityEndLabel' })}
            value={formatDate(unitOrder?.validity_end)}
          />
        </BlurredContainerM2030>
      </div>
      {unitOrder?.status === Status.Active || unitOrder?.status === Status.Partial ? (
        <Button secondary onClick={handleCancelClick}>
          <FormattedMessage id={'unitOrders.details.cancelOrderButton'} />
        </Button>
      ) : null}
    </DefaultScreen>
  );
};

export default withErrorBoundary(UnitOrderDetails, { fallback: Fallback });
